import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService, UserProfile } from '../services/firestore';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAdmin } from '../contexts/AdminContext';
import {
  needsShopifyInstallWizard,
  requiresShopifyComplianceOnboarding,
} from '../utils/shopifyComplianceOnboarding';
import { shouldSkipEmailVerificationForShopifyInstall } from '../utils/shopifyTrustEmail';
import { resolveShopifyInstallWizardRedirect } from '../utils/shopifyInstallWizardNavigation';


interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { currentUser, isNewUser, resetNewUserFlag, isEmailVerifiedCustom } = useAuth();
  const { isShopifyEmbedded } = useWorkspace();
  const { isAdmin, isCheckingAdmin } = useAdmin();
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Don't make any decisions until we know if the user is an admin
      if (isCheckingAdmin || isAdmin === null) {
        return;
      }

      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      let isVerified = false;

      // ADMIN BYPASS: Administrators do not need to verify their email in the custom system
      if (isAdmin === true) {
        console.log('🛡️ OnboardingGuard: Admin detected, bypassing email verification');
        isVerified = true;
        setEmailVerified(true);
        // Continue to onboarding checks
      } else {
        const profileEarly = await userService.getUserProfile(currentUser.uid);
        if (shouldSkipEmailVerificationForShopifyInstall(profileEarly)) {
          console.log('🛡️ OnboardingGuard: Shopify install — skipping email passcode verification');
          isVerified = true;
        } else {
        // ALWAYS check custom email verification status for ALL users
      // This prevents the "refresh to bypass" vulnerability where isNewUser resets to false
      console.log('🛡️ OnboardingGuard: Checking verification status for:', currentUser.email);
      isVerified = await isEmailVerifiedCustom();
      console.log('🛡️ OnboardingGuard: Custom verification result:', isVerified);

      // Fallback: If custom verification fails, check Firebase Auth verification
      // This handles cases where user verified via link or provider (Google) but custom doc is missing/delayed
      if (!isVerified && currentUser.emailVerified) {
        console.log('🛡️ OnboardingGuard: Custom check failed, but Firebase Auth says verified. allowing access.');
        isVerified = true;
      }

      // Double check if initially false - prevents efficient flickering if status just updated
      if (!isVerified) {
        console.log('🛡️ OnboardingGuard: Waiting 1.5s to recheck...');
        await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5s
        isVerified = await isEmailVerifiedCustom(); // Check again

        // Check Firebase Auth again too
        if (!isVerified && currentUser.emailVerified) {
          isVerified = true;
        }
        console.log('🛡️ OnboardingGuard: Recheck result:', isVerified);
      }
        }

      setEmailVerified(isVerified);
    }

      if (!isVerified) {
        console.log('🛡️ OnboardingGuard: User not verified, redirecting to /verify-email');
        setIsLoading(false);
        return; // EmailVerificationRequired component will handle this
      }

      // Check onboarding status
      try {
        const profile = await userService.getUserProfile(currentUser.uid);
        console.log('🔍 OnboardingGuard - Profile data:', profile);
        console.log('🔍 OnboardingGuard - emailVerified:', isVerified);


        if (!profile || !profile.profileCompleted) {
          console.log('🔍 OnboardingGuard - Profile missing or not completed, redirecting to create profile');
          setShouldRedirect('/onboarding/profile');
          setIsLoading(false);
          return;
        }

        if (needsShopifyInstallWizard(profile)) {
          console.log('🔍 OnboardingGuard - Resuming Shopify install wizard');
          const resumePath = await resolveShopifyInstallWizardRedirect(currentUser.uid, profile);
          if (resumePath) {
            setShouldRedirect(resumePath);
            setIsLoading(false);
            return;
          }
        }

        console.log(
          '🔍 OnboardingGuard - requiresShopifyComplianceOnboarding:',
          requiresShopifyComplianceOnboarding(profile, isShopifyEmbedded)
        );

        if (requiresShopifyComplianceOnboarding(profile, isShopifyEmbedded)) {
          if (!profile.addressCompleted) {
            console.log('🔍 OnboardingGuard - Shopify address step needed');
            setShouldRedirect('/shopify/onboarding/address');
            setIsLoading(false);
            return;
          }
          if (!profile.syncCompleted) {
            console.log('🔍 OnboardingGuard - Shopify sync step needed');
            setShouldRedirect('/shopify/onboarding/sync');
            setIsLoading(false);
            return;
          }
          if (!profile.brandingCompleted) {
            console.log('🔍 OnboardingGuard - Shopify branding step needed');
            setShouldRedirect('/shopify/onboarding/branding');
            setIsLoading(false);
            return;
          }
        } else if (!profile.workspaceCompleted) {
          // If profile is complete but workspace is not, redirect to workspace
          setShouldRedirect('/onboarding/workspace');
          setIsLoading(false);
          return;
        } else {
          // All onboarding steps are complete, reset the new user flag
          if (isNewUser) {
            resetNewUserFlag();
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // If there's an error, assume onboarding is needed
        setNeedsOnboarding(true);
        setShouldRedirect('/onboarding/profile');
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [
    currentUser,
    isNewUser,
    resetNewUserFlag,
    isEmailVerifiedCustom,
    isAdmin,
    isCheckingAdmin,
    isShopifyEmbedded,
  ]);

  // Show loading state while checking onboarding status
  // This prevents dashboard from flashing before redirect
  if (isLoading || isCheckingAdmin || isAdmin === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          {isCheckingAdmin && <p className="mt-2 text-xs text-slate-500">Checking admin privileges...</p>}
        </div>
      </div>
    );
  }

  // If user is not logged in, redirect to home
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // If email is not verified, redirect to /verify-email
  // We check this for ALL users now, not just new ones
  if (!emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // If we determined a redirect is needed, do it immediately
  if (shouldRedirect) {
    return <Navigate to={shouldRedirect} replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding/profile" replace />;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
